import { api } from '@/lib/api';
import type {
  TicketPipelineResponse,
  CreateTicketPipelineRequest,
  UpdateTicketPipelineRequest,
  TicketPipelineQuery,
  TicketPipelineListResponse,
  ReorderTicketPipelinesRequest,
  TicketPipelineDeleteValidation,
  TicketStageResponse,
  CreateTicketStageRequest,
  UpdateTicketStageRequest,
  TicketStageQuery,
  TicketStageListResponse,
  ReorderTicketStagesRequest,
  TicketStageDeleteValidation
} from '@troia-v3/types';

export const pipelinesApi = {
  // ============================================================================
  // PIPELINES CRUD
  // ============================================================================

  async getPipelines(query?: TicketPipelineQuery): Promise<TicketPipelineListResponse> {
    const response = await api.get<{ success: boolean; data: TicketPipelineListResponse }>('ticket-pipelines', { params: query });
    return response.data.data;
  },

  async getPipelineById(id: string): Promise<TicketPipelineResponse> {
    const response = await api.get<{ success: boolean; data: TicketPipelineResponse }>(`ticket-pipelines/${id}`);
    return response.data.data;
  },

  async createPipeline(data: CreateTicketPipelineRequest): Promise<TicketPipelineResponse> {
    const response = await api.post<{ success: boolean; data: TicketPipelineResponse }>('ticket-pipelines', data);
    return response.data.data;
  },

  async updatePipeline(id: string, data: UpdateTicketPipelineRequest): Promise<TicketPipelineResponse> {
    const response = await api.put<{ success: boolean; data: TicketPipelineResponse }>(`ticket-pipelines/${id}`, data);
    return response.data.data;
  },

  async deletePipeline(id: string): Promise<void> {
    await api.delete(`ticket-pipelines/${id}`);
  },

  async validateDeletePipeline(id: string): Promise<TicketPipelineDeleteValidation> {
    const response = await api.get<{ success: boolean; data: TicketPipelineDeleteValidation }>(`ticket-pipelines/validate-delete/${id}`);
    return response.data.data;
  },

  async deletePipelineWithValidation(id: string): Promise<void> {
    await api.delete(`ticket-pipelines/${id}/with-validation`);
  },

  async reorderPipelines(data: ReorderTicketPipelinesRequest): Promise<TicketPipelineResponse[]> {
    const response = await api.post<{ success: boolean; data: TicketPipelineResponse[] }>('ticket-pipelines/reorder', data);
    return response.data.data;
  },

  async getPipelinesForDropdown(): Promise<TicketPipelineResponse[]> {
    const response = await api.get<{ success: boolean; data: TicketPipelineResponse[] }>('ticket-pipelines/dropdown');
    return response.data.data;
  },

  // ============================================================================
  // STAGES CRUD
  // ============================================================================

  async getStages(query?: TicketStageQuery): Promise<TicketStageListResponse> {
    const response = await api.get<{ success: boolean; data: TicketStageListResponse }>('ticket-stages', { params: query });
    return response.data.data;
  },

  async getStageById(id: string): Promise<TicketStageResponse> {
    const response = await api.get<{ success: boolean; data: TicketStageResponse }>(`ticket-stages/${id}`);
    return response.data.data;
  },

  async getStagesByPipelineId(pipelineId: string): Promise<TicketStageResponse[]> {
    const response = await api.get<{ success: boolean; data: TicketStageResponse[] }>(`ticket-stages/by-pipeline/${pipelineId}`);
    return response.data.data;
  },

  async createStage(data: CreateTicketStageRequest): Promise<TicketStageResponse> {
    const response = await api.post<{ success: boolean; data: TicketStageResponse }>('ticket-stages', data);
    return response.data.data;
  },

  async updateStage(id: string, data: UpdateTicketStageRequest): Promise<TicketStageResponse> {
    const response = await api.put<{ success: boolean; data: TicketStageResponse }>(`ticket-stages/${id}`, data);
    return response.data.data;
  },

  async deleteStage(id: string): Promise<void> {
    await api.delete(`ticket-stages/${id}`);
  },

  async validateDeleteStage(id: string): Promise<TicketStageDeleteValidation> {
    const response = await api.get<{ success: boolean; data: TicketStageDeleteValidation }>(`ticket-stages/validate-delete/${id}`);
    return response.data.data;
  },

  async deleteStageWithValidation(id: string): Promise<void> {
    await api.delete(`ticket-stages/${id}/with-validation`);
  },

  async reorderStages(data: ReorderTicketStagesRequest): Promise<TicketStageResponse[]> {
    const response = await api.post<{ success: boolean; data: TicketStageResponse[] }>('ticket-stages/reorder', data);
    return response.data.data;
  },

  async activateStage(id: string): Promise<TicketStageResponse> {
    const response = await api.patch<{ success: boolean; data: TicketStageResponse }>(`ticket-stages/${id}/activate`);
    return response.data.data;
  },

  async deactivateStage(id: string): Promise<TicketStageResponse> {
    const response = await api.patch<{ success: boolean; data: TicketStageResponse }>(`ticket-stages/${id}/deactivate`);
    return response.data.data;
  }
};
